import React from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useLanguage } from '../context/LanguageContext';

// CSS für Leaflet-Logo verstecken (nur die Grafiken, nicht den Text)
const leafletStyles = `
  .leaflet-control-attribution img {
    display: none !important;
  }
  .leaflet-control-attribution svg {
    display: none !important;
  }
`;

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Hilfskomponente um die Karte von außen zu steuern
const MapController = ({ flyTo }) => {
  const map = useMap();
  React.useEffect(() => {
    if (flyTo) {
      map.flyTo(flyTo, 13, { duration: 1.2 });
    }
  }, [flyTo, map]);
  return null;
};

const MarketsPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [selectedPosition, setSelectedPosition] = React.useState(null);
  const markerRefs = React.useRef({});
  const initialFocusDone = React.useRef(false);

  // Leaflet-Logo verstecken
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = leafletStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const markets = [
    {
      id: 1,
      name: t('markets.farmShopName'),
      position: [64.980138, 19.851714], // Svansele
      description: t('markets.farmShop'),
      color: 'bg-green-100 border-green-300'
    },
    {
      id: 2,
      name: t('markets.lovangerName'),
      position: [64.369206, 21.314441], // Lövånger
      description: t('markets.lovanger'),
      link: { text: t('markets.website'), url: 'http://www.lovangersaluhall.se/' },
      color: 'bg-blue-100 border-blue-300'
    },
    {
      id: 3,
      name: t('markets.burtraskName'),
      position: [64.520847, 20.658186], // Burträsk
      description: t('markets.burtrask'),
      link: { text: t('markets.website'), url: 'http://www.burtrasksaluhall.se/' },
      color: 'bg-yellow-100 border-yellow-300'
    },
    {
      id: 4,
      name: t('markets.skellefteaName'),
      position: [64.736921, 20.967375], // Skellefteå
      description: t('markets.skelleftea'),
      link: { text: 'Facebook', url: 'https://www.facebook.com/groups/346087959537528/' },
      color: 'bg-purple-100 border-purple-300'
    },
    {
      id: 5,
      name: t('markets.kusforsName'),
      position: [64.940265, 19.994923], // Kusfors
      description: t('markets.kusfors'),
      color: 'bg-red-100 border-red-300'
    },
    {
      id: 6,
      name: t('markets.adakName'),
      position: [65.349452, 18.591593], // Adak
      description: t('markets.adak'),
      color: 'bg-orange-100 border-orange-300'
    },
    {
      id: 7,
      name: t('markets.glommerstrakName'),
      position: [65.260753, 19.633828], // Glommersträsk
      description: t('markets.glommerstrak'),
      color: 'bg-teal-100 border-teal-300'
    },
    {
      id: 8,
      name: t('markets.farmupName'),
      description: t('markets.farmup'),
      link: { text: 'farmup.se', url: 'https://www.farmup.se/' },
      color: 'bg-lime-100 border-lime-300'
    }
  ];

  // Automatisch auf Gårdsbutiken zoomen wenn ?focus=farmshop
  React.useEffect(() => {
    if (initialFocusDone.current) return;
    const focus = searchParams.get('focus');
    if (focus === 'farmshop') {
      initialFocusDone.current = true;
      const farmShop = markets.find((m) => m.id === 1);
      if (farmShop) {
        setTimeout(() => {
          setSelectedPosition([...farmShop.position]);
          setTimeout(() => {
            const marker = markerRefs.current[farmShop.id];
            if (marker) marker.openPopup();
          }, 1300);
        }, 500);
      }
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t('nav.markets')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('whyWe.localRaw.desc')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-[800px] lg:h-[600px]">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="rounded-lg overflow-hidden shadow-lg h-[400px] lg:h-auto border-4 border-white z-0"
          >
            <MapContainer 
              center={[64.75, 19.5]} 
              zoom={7} 
              scrollWheelZoom={false} 
              style={{ height: '100%', width: '100%' }}
            >
              <MapController flyTo={selectedPosition} />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {markets.filter((market) => market.position).map((market) => (
                <Marker
                  key={market.id}
                  position={market.position}
                  ref={(ref) => { markerRefs.current[market.id] = ref; }}
                >
                  <Popup>
                    <strong>{market.name}</strong>
                    <br />
                    <span style={{ fontSize: '0.85em' }}>{market.description}</span>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </motion.div>

          {/* List */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="overflow-y-auto pr-4 space-y-4 h-[400px] lg:h-full custom-scrollbar"
          >
            {markets.map((market) => (
              <div
                key={market.id}
                className={`p-6 rounded-lg border-l-4 shadow-md bg-white hover:shadow-lg transition-all transform hover:-translate-y-1 ${market.position ? 'cursor-pointer' : ''} ${market.color}`}
                onClick={() => {
                  if (!market.position) return;
                  setSelectedPosition([...market.position]);
                  setTimeout(() => {
                    const marker = markerRefs.current[market.id];
                    if (marker) marker.openPopup();
                  }, 1300);
                }}
              >
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {market.name}
                </h3>
                <p className="text-gray-600">
                  <span className="font-semibold">{market.name}</span> {market.description} {market.link && (
                    <a href={market.link.url} target="_blank" rel="noopener noreferrer" className="text-accent-green hover:underline font-semibold ml-1">
                      {market.link.text}
                    </a>
                  )}
                </p>
              </div>
            ))}

            <div className="bg-primary-green text-white p-6 rounded-lg shadow-lg mt-8">
              <h3 className="text-xl font-bold mb-2">
                {t('markets.wholesaleTitle')}
              </h3>
              <p className="mb-4">
                {t('markets.wholesaleText')}
              </p>
              <Link 
                to="/storhandla" 
                className="inline-block bg-white text-primary-green px-6 py-2 rounded-lg font-bold hover:bg-gray-100 transition-colors"
              >
                {t('markets.wholesaleBtn')}
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default MarketsPage;